import { Controller } from 'stimulus';
import { French } from "flatpickr/dist/l10n/fr.js"
import { Spanish } from "flatpickr/dist/l10n/es.js"
import { Arabic } from "flatpickr/dist/l10n/ar.js"
import { Japanese } from "flatpickr/dist/l10n/ja.js"
import { Korean } from "flatpickr/dist/l10n/ko.js"
import { Mandarin } from "flatpickr/dist/l10n/zh.js"
import { Italian } from "flatpickr/dist/l10n/it.js"
import { Dutch } from "flatpickr/dist/l10n/nl.js"
import { German } from "flatpickr/dist/l10n/de.js"
import { Portuguese } from "flatpickr/dist/l10n/pt.js"

export default class extends Controller {

  connect() {
    const defaultCurrentTime = this.element.dataset.defaultCurrentTime === 'true'; // Check if default_current_time is true.
    const calendarColor = this.element.dataset.calendarColor;  // Fetch the dynamic calendar color
    const { accountId, projectId, taskListId, taskId, blockId, flatpickrUserTimezone } = this.element.dataset;
    let dataSetDateFormat = this.element.dataset.dateFormat;
    if (dataSetDateFormat !== undefined) {
      dataSetDateFormat = dataSetDateFormat.replace(/%/g, '') + " H:i"
    }
    // Function to calculate the user's local time using the time zone offset
    const getUserTimeZoneTime = () => {
      const currentDate = new Date();
      const localOffset = currentDate.getTimezoneOffset(); // Get the local timezone offset in minutes
      const userOffset = new Date().getTimezoneOffset(); // Get the user's time zone offset in minutes

      // Convert time to the user's time zone
      const userTime = new Date(currentDate.getTime() - (localOffset - userOffset) * 60000);

      return { hour: userTime.getHours(), minute: userTime.getMinutes() };
    };

    const userTime = getUserTimeZoneTime();
    const userLocale = document.querySelector("meta[name='current-locale']")?.content || "en";
    const localeMapping = {
      "fr": French,
      "es": Spanish,
      "ar": Arabic,
      "ja": Japanese,
      "de": German,
      "zh": Mandarin,
      "ko": Korean,
      "it": Italian,
      "pt": Portuguese,
      "nl": Dutch,
    };

    flatpickr(this.element, {
      enableTime: true,
      animate: false,
      wrap: true,
      disableMobile: "true",
      dateFormat: "Y-m-d H:i",
      defaultHour: defaultCurrentTime ? userTime.hour : 12,
      defaultMinute: defaultCurrentTime ? userTime.minute : 0,
      locale: localeMapping[userLocale] || "en",

      onOpen: (dateStr, selectedDates, instance) => {
        if (instance.element.classList.contains("has-value")) {
          this.element.querySelectorAll(".display-datetime .datetime-actions .icon.clear").forEach((el) => {
            el.classList.remove("hidden");
          });
        } else {
          this.element.querySelectorAll(".display-datetime .datetime-actions .icon.clear").forEach((el) => {
            el.classList.add("hidden");
          });
        }

        // Prevent popup reopening when the clear icon is clicked
        const clearButtons = this.element.querySelectorAll(".datetime-actions .icon.clear");
        clearButtons.forEach((clearButton) => {
          if (!clearButton.dataset.listenerAdded) {
            clearButton.addEventListener('click', (e) => {
              console.log("Close clicked");
              e.stopPropagation();
            });
            clearButton.dataset.listenerAdded = "true";
          }
        });

        if (calendarColor) {
          applySelectedDateStyles(instance, calendarColor);
        }
      },

      onChange: (selectedDates, dateStr, instance) => {
        const dateDifferenceElement = this.element.querySelector(".date-difference-value");
        const clearIcon = this.element.querySelector(".display-datetime .datetime-actions .icon.clear");

        if (calendarColor) {
          applySelectedDateStyles(instance, calendarColor);
        }

        this.updateDisplay(dateStr);
        if (selectedDates.length === 0) {
          clearIcon.classList.add("hidden");
          const just_now = document.querySelector('meta[name="i18n-just_now"]')?.content || 'Just Now';
          if (dateDifferenceElement) { dateDifferenceElement.innerHTML = just_now; }
        } else {
          clearIcon.classList.remove("hidden");
          if (dateDifferenceElement) {
            const niceDateDifference = this.calculateNiceDateDifference(instance.selectedDates[0], flatpickrUserTimezone);
            dateDifferenceElement.innerHTML = niceDateDifference;
          }
        }
      },

      onReady: (selectedDates, dateStr, instance) => {
        const updateLabel = document.querySelector('meta[name="i18n-update"]')?.content || 'Update';
        const footer = document.createElement('div');
        footer.style.cssText = `
          text-align: center;
          margin-bottom: 10px;

        `;

        footer.innerHTML = `
          <button type="button" style="
            border-radius: 2px;
            font-size: 14px;
            background-color: ${calendarColor} !important;
          ">
            ${updateLabel}
          </button>
        `;

        instance.calendarContainer.appendChild(footer);

        footer.querySelector('button').addEventListener('click', () => {
          instance.close();
        });

        if (calendarColor) {
          applySelectedDateStyles(instance, calendarColor);
        }
      },

      onClose: (selectedDates, dateStr, instance) => {
        if (blockId) {
          let formattedDate = ""

          if (instance.selectedDates.length > 0) {
            formattedDate = this.formatDateTime(instance.selectedDates[0]);
          }

          this.updateDateTime(formattedDate, blockId, accountId, projectId, taskListId, taskId);
        }
      },
    });
  }

  // Method to calculate nice date difference
  calculateNiceDateDifference(selectedDate, flatpickrUserTimezone) {
    const currentDate = new Date(new Date().toLocaleString('en-US', { timeZone: flatpickrUserTimezone }));
    const timeDifference = currentDate - selectedDate;
    let result = '';

    const secondsInMinute = 60;
    const secondsInHour = 3600;
    const secondsInDay = 86400;
    const secondsInWeek = 604800;
    const secondsInMonth = 2592000;
    const secondsInYear = 31536000;

    function t(key) {
        return document.querySelector(`meta[name="i18n-${key}"]`)?.content || key;
      }

    if (timeDifference < secondsInMinute * 1000) {
      result = t("just-now");
    } else if (timeDifference < secondsInHour * 1000) {
      result = `${Math.floor(timeDifference / (secondsInMinute * 1000))} ${t("minutes")} ${t('ago')}`;
    } else if (timeDifference < secondsInDay * 1000) {
      result = `${Math.floor(timeDifference / (secondsInHour * 1000))} ${t("hours")} ${t('ago')}`;
    } else if (timeDifference < secondsInWeek * 1000) {
      result = `${Math.floor(timeDifference / (secondsInDay * 1000))} ${t("days")} ${t('ago')}`;
    } else if (timeDifference < secondsInMonth * 1000) {
      result = `${Math.floor(timeDifference / (secondsInWeek * 1000))} ${t("weeks")} ${t('ago')}`;
    } else if (timeDifference < secondsInYear * 1000) {
      result = `${Math.floor(timeDifference / (secondsInMonth * 1000))} ${t("months")} ${t('ago')}`;
    } else {
      result = `${Math.floor(timeDifference / (secondsInYear * 1000))} ${t("years")} ${t('ago')}`;
    }

    if (timeDifference < 0) {
      if (Math.abs(timeDifference) < secondsInMinute * 1000) {
        result = t("just-now");
      } else if (Math.abs(timeDifference) < secondsInHour * 1000) {
        result = `${Math.floor(Math.abs(timeDifference) / (secondsInMinute * 1000))} ${t("minutes")} ${t('from-now')}`;
      } else if (Math.abs(timeDifference) < secondsInDay * 1000) {
        result = `${Math.floor(Math.abs(timeDifference) / (secondsInHour * 1000))} ${t("hours")} ${t('from-now')}`;
      } else if (Math.abs(timeDifference) < secondsInWeek * 1000) {
        result = `${Math.floor(Math.abs(timeDifference) / (secondsInDay * 1000))} ${t("days")} ${t('from-now')}`;
      } else if (Math.abs(timeDifference) < secondsInMonth * 1000) {
        result = `${Math.floor(Math.abs(timeDifference) / (secondsInWeek * 1000))} ${t("weeks")} ${t('from-now')}`;
      } else if (Math.abs(timeDifference) < secondsInYear * 1000) {
        result = `${Math.floor(Math.abs(timeDifference) / (secondsInMonth * 1000))} ${t("months")} ${t('from-now')}`;
      } else {
        result = `${Math.floor(Math.abs(timeDifference) / (secondsInYear * 1000))} ${t("years")} ${t('from-now')}`;
      }
    }

    if (Math.abs(timeDifference) < secondsInDay * 1000) {
      const tomorrow = new Date(currentDate.getTime() + secondsInDay * 1000);
      if (selectedDate.getTime() === tomorrow.getTime()) {
        result = t("tomorrow");
      }
    }

    if (Math.abs(timeDifference) < secondsInDay * 1000 * 2) {
      const dayAfterTomorrow = new Date(currentDate.getTime() + secondsInDay * 1000 * 2);
      if (selectedDate.getTime() === dayAfterTomorrow.getTime()) {
        result = "day after tomorrow";
      }
    }

    return result;
  }

  async updateDateTime(selectedDate, blockId, accountId, projectId, taskListId, taskId) {
    try {
      const response = await fetch(`/${accountId}/projects/${projectId}/task_lists/${taskListId}/tasks/${taskId}/task_blocks/${blockId}/update_content`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({ value: selectedDate })
      });

      if (response.ok) {
        const jsonResponse = await response.json();
        this.updateDisplay(jsonResponse.value);
      }
    } catch (error) {
      console.error("Error updating datetime:", error);
    }
  }

  updateDisplay(updatedValue) {
    const dateElement = this.element.querySelector(".display-datetime .datetime .date");
    const timeElement = this.element.querySelector(".display-datetime .datetime .time");
    const dateFormat = this.element.dataset.dateFormat || "%b %e, %Y";

    if (!updatedValue) {
      dateElement.innerHTML = "";
      timeElement.innerHTML = "";
      return;
    }

    const updatedDate = new Date(updatedValue);

    if (isNaN(updatedDate.getTime())) {
      console.error("updateDisplay: Invalid date value", updatedValue);
      return;
    }

    if (dateElement) {
      const flatpickrFormat = this.convertRailsDateFormat(dateFormat);
      dateElement.textContent = flatpickr.formatDate(updatedDate, flatpickrFormat);
    }

    if (timeElement) {
      timeElement.textContent = updatedDate.toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit', hour12: true
      });
    }
  }

  convertRailsDateFormat(railsFormat) {
    const formatMap = {
      "%Y": "Y",  // Year (4-digit)
      "%y": "y",  // Year (2-digit)
      "%m": "m",  // Month (zero-padded)
      "%-m": "n", // Month (no padding)
      "%b": "M",  // Abbreviated month (e.g., "Jan")
      "%B": "F",  // Full month name (e.g., "January")
      "%d": "d",  // Day (zero-padded)
      "%-d": "j", // Day (no padding)
      "%e": "j",  // Day (no padding, same as `%-d`)
      "%A": "l",  // Full weekday name (e.g., "Monday")
    };

    let flatpickrFormat = railsFormat;
    Object.keys(formatMap).forEach((key) => {
      flatpickrFormat = flatpickrFormat.replace(new RegExp(key, "g"), formatMap[key]);
    });

    return flatpickrFormat;
  }

  formatDateTime(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure 2 digits
    const day = String(date.getDate()).padStart(2, "0"); // Ensure 2 digits
    const hours = String(date.getHours()).padStart(2, "0"); // Ensure 2 digits
    const minutes = String(date.getMinutes()).padStart(2, "0"); // Ensure 2 digits

    return `${year}-${month}-${day} ${hours}:${minutes}`; // "YYYY-MM-DD HH:mm"
  }
}

function applySelectedDateStyles(instance, calendarColor) {
  const selectedDate = instance.calendarContainer.querySelector('.flatpickr-day.selected');
  if (selectedDate) {
    selectedDate.style.backgroundColor = calendarColor;
    selectedDate.style.color = '#fff';
  }
}
